import React, { useRef, useEffect } from "react";
import {
  Box,
  HStack,
  Text,
  Image,
  Center,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import { QRCodeCanvas as QRCode } from "qrcode.react";

const IDCardComponent = ({ userData, qrCodeData, onDownload }) => {
  const cardRef = useRef();

  // Default user data structure
  const defaultUser = {
    user_name: "",
    designation: "",
    employee_id: "",
    blood_grp: "",
    user_phone: "",
    user_email: "",
    user_pic: "",
    user_emergency_contact: "",
    office_contact: "",
    office_address: "",
  };

  const user = { ...defaultUser, ...userData };

  // Debug QR code value - Force capital C
  const qrValue =
    qrCodeData ||
    `${window.location.origin}/idCard/${user.user_email?.replace(
      "@ba-systems.com",
      ""
    ) || 'unknown'}`;

  const correctedQrValue = qrValue.replace("/idcard/", "/idCard/");

  useEffect(() => {}, [qrValue, correctedQrValue, user.user_email, qrCodeData]);

  if (!user.user_name) {
    return (
      <Center minH="100vh" bg="gray.100">
        <Alert status="error" maxW="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Error!</AlertTitle>
            <AlertDescription>User data not found.</AlertDescription>
          </Box>
        </Alert>
      </Center>
    );
  }

  return (
    <Box
      minH="100vh"
      w="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="#e4e4e4"
      p={{ base: "10px", md: "20px" }}
      overflow="auto"
    >
      <HStack
        spacing={{ base: "10px", md: "20px" }}
        ref={cardRef}
        flexDirection={{ base: "column", lg: "row" }}
        align="flex-start"
        justify="center"
        maxW="100%"
      >
        {/* Front Side */}
        <Box
          className="ebs-id-card-container"
          position="relative"
          w={{ base: "280px", md: "300px" }}
          h={{ base: "448px", md: "480px" }}
          maxW="90vw"
          borderRadius="10px"
          overflow="hidden"
          transform={{ base: "scale(0.9)", sm: "scale(1)" }}
          transformOrigin="center"
        >
          <Box
            className="id-card-front"
            position="relative"
            background="url('/svg/id_front.svg') no-repeat top center"
            backgroundSize="100% 100%"
            backgroundColor="#000B8C"
            w={{ base: "280px", md: "300px" }}
            h={{ base: "450px", md: "486px" }}
            display="flex"
            color="white"
            fontFamily="'Fira Sans', sans-serif"
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-start"
          >
            {/* Logo */}
            <Box
              as="a"
              className="id-logo"
              href="#"
              mt={{ base: "20px", md: "30px" }}
              display="block"
              mb={{ base: "10px", md: "15px" }}
              w={{ base: "100px", md: "115px" }}
            >
              <Image src="/png/BA.png" alt="logo" w="100%" />
            </Box>

            {/* Conditional spacing based on blood group */}
            {user.blood_grp ? (
              <Box h={{ base: "30px", md: "40px" }} />
            ) : (
              <Box h={{ base: "45px", md: "60px" }} />
            )}

            <Box
              className="id-photo"
              border="2px solid #2A5894"
              borderRadius="20px"
              overflow="hidden"
              h={{ base: "260px", md: "180px" }}
              w={{ base: "115px", md: "135px" }}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {user.user_pic ? (
                <Image
                  src={`https://ebs.ba-systems.com/users/upload/${user.user_pic}`}
                  alt="Profile"
                  w="100%"
                  h="100%"
                  objectFit="cover"
                />
              ) : (
                <Image
                  src="/assets/images/default_profile.jpg"
                  alt="Profile"
                  w="100%"
                  h="100%"
                  objectFit="cover"
                />
              )}
            </Box>
            <Box
              className="id-title"
              w="100%"
              p={{ base: "15px 20px 5px", md: "20px 25px 5px" }}
              textAlign="center"
            >
              <Text
                as="strong"
                fontFamily="'Fira Sans', sans-serif"
                fontSize={{ base: "16px", md: "18px" }}
                lineHeight="1"
                fontWeight="600"
                mb="0"
              >
                {user.user_name}
              </Text>
              <br />
              <Box h={{ base: "2px", md: "4px" }} />
              <Text
                fontFamily="'Fira Sans', sans-serif"
                fontSize={{ base: "10px", md: "12px" }}
              >
                {user.designation}
              </Text>
            </Box>

            {/* Details List */}
            <Box
              className="id-desc-list"
              w="100%"
              p={{ base: "0 15px 0 25px", md: "0 20px 0 30px" }}
              textAlign="left"
            >
              <Box
                as="ul"
                className="id-list"
                p="0"
                m="0"
                listStyleType="none"
                mb={{ base: "15px", md: "20px" }}
              >
                <Box h={{ base: "8px", md: "10px" }} />

                <Box
                  as="li"
                  display="flex"
                  alignItems="center"
                  w="100%"
                  textAlign="left"
                  fontFamily="'Fira Sans', sans-serif"
                  fontSize={{ base: "11px", md: "12px" }}
                  lineHeight="2"
                >
                  <Box
                    as="span"
                    w={{ base: "18px", md: "20px" }}
                    h={{ base: "18px", md: "20px" }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mr="6px"
                  >
                    <Image
                      src="/svg/id_id.svg"
                      alt="icon"
                      w={{ base: "16px", md: "18px" }}
                      h={{ base: "16px", md: "18px" }}
                    />
                  </Box>
                  <Text as="span" mr="6px">
                    :
                  </Text>
                  <Text as="span">{user.employee_id}</Text>
                </Box>

                {user.blood_grp && (
                  <Box
                    as="li"
                    display="flex"
                    alignItems="center"
                    w="100%"
                    textAlign="left"
                    fontFamily="'Fira Sans', sans-serif"
                    fontSize={{ base: "11px", md: "12px" }}
                    lineHeight="2"
                  >
                    <Box
                      as="span"
                      w={{ base: "18px", md: "20px" }}
                      h={{ base: "18px", md: "20px" }}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mr="6px"
                    >
                      <Image
                        src="/svg/id_blood.svg"
                        alt="icon"
                        w={{ base: "16px", md: "18px" }}
                        h={{ base: "16px", md: "18px" }}
                      />
                    </Box>
                    <Text as="span" mr="6px">
                      :
                    </Text>
                    <Text as="span">{user.blood_grp}</Text>
                  </Box>
                )}

                <Box
                  as="li"
                  display="flex"
                  alignItems="center"
                  w="100%"
                  textAlign="left"
                  fontFamily="'Fira Sans', sans-serif"
                  fontSize={{ base: "11px", md: "12px" }}
                  lineHeight="2"
                >
                  <Box
                    as="span"
                    w={{ base: "18px", md: "20px" }}
                    h={{ base: "18px", md: "20px" }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mr="6px"
                  >
                    <Image
                      src="/svg/id_call.svg"
                      alt="icon"
                      w={{ base: "16px", md: "18px" }}
                      h={{ base: "16px", md: "18px" }}
                    />
                  </Box>
                  <Text as="span" mr="6px">
                    :
                  </Text>
                  <Text as="span">{user.user_phone}</Text>
                </Box>

                <Box
                  as="li"
                  display="flex"
                  alignItems="center"
                  w="100%"
                  textAlign="left"
                  fontFamily="'Fira Sans', sans-serif"
                  fontSize={{ base: "11px", md: "12px" }}
                  lineHeight="2"
                >
                  <Box
                    as="span"
                    w={{ base: "18px", md: "20px" }}
                    h={{ base: "18px", md: "20px" }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mr="6px"
                  >
                    <Image
                      src="/svg/id_sms.svg"
                      alt="icon"
                      w={{ base: "16px", md: "18px" }}
                      h={{ base: "16px", md: "18px" }}
                    />
                  </Box>
                  <Text as="span" mr="6px">
                    :
                  </Text>
                  <Text as="span">{user.user_email}</Text>
                </Box>
              </Box>
            </Box>

            <Box
              h={{ base: "20px", md: "30px" }}
              // w={{ base: "56px", md: "66px" }}
              display="block"
              m={{ base: "0 auto 8px", md: "0 auto 10px" }}
            ></Box>
          </Box>
        </Box>

        {/* Back Side */}
        <Box
          className="ebs-id-card-container"
          position="relative"
          w={{ base: "280px", md: "300px" }}
          h={{ base: "450px", md: "486px" }}
          maxW="90vw"
          borderRadius="10px"
          overflow="hidden"
          transform={{ base: "scale(0.9)", sm: "scale(1)" }}
          transformOrigin="center"
        >
          <Box
            className="id-card-back"
            position="relative"
            background="url('/svg/id_back.svg') no-repeat top center"
            backgroundSize="100% 100%"
            backgroundColor="#000B8C"
            w={{ base: "280px", md: "300px" }}
            h={{ base: "448px", md: "478px" }}
            display="flex"
            color="white"
            borderRadius={10}
            fontFamily="'Fira Sans', sans-serif"
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-start"
          >
            {/* Logo */}
            <Box
              as="a"
              className="id-logo"
              href="#"
              mt={{ base: "20px", md: "30px" }}
              display="block"
              mb={{ base: "10px", md: "15px" }}
              w={{ base: "100px", md: "115px" }}
            >
              <Image src="/png/BA.png" alt="logo" w="100%" />
            </Box>

            <Box h={{ base: "15px", md: "20px" }} />

            {/* QR Code - Back */}
            <Box
              className="id-qr-box"
              h={{ base: "80px", md: "100px" }}
              // border="1px solid red"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="white"
              p="5px"
              mb={5}
            >
              {correctedQrValue ? (
                <QRCode
                  value={correctedQrValue}
                  size={90}
                  bgColor="#ffffff"
                  fgColor="#000000"
                />
              ) : (
                <Text color="black">QR Code Loading...</Text>
              )}
            </Box>

            {/* Emergency Contact Personal */}
            <Box
              className="id-desc-list"
              w="100%"
              p={{ base: "0 15px 0 25px", md: "0 20px 0 30px" }}
              textAlign="left"
            >
              <Text
                as="h4"
                className="id-desc-title"
                textAlign="left"
                fontFamily="'Fira Sans', sans-serif"
                fontSize={{ base: "11px", md: "12px" }}
                lineHeight="1.2"
                fontWeight="600"
                m="0 0 3px"
              >
                For Emergency Contact (Personal):
              </Text>
              <Box as="ul" className="id-list" p="0" m="0" listStyleType="none">
                <Box
                  as="li"
                  display="inline-block"
                  w="100%"
                  position="relative"
                  // pl={{ base: "35px", md: "40px" }}
                  textAlign="left"
                  fontFamily="'Fira Sans', sans-serif"
                  fontSize={{ base: "11px", md: "12px" }}
                  lineHeight="2"
                >
                  <Box
                    as="span"
                    position="absolute"
                    left="0"
                    top="0"
                    // w={{ base: "35px", md: "40px" }}
                  >
                    {/* <Image
                      src="/asset/images/idcard/call_logo.svg"
                      alt="icon"
                      position="absolute"
                      pt="3px"
                      w={{ base: "12px", md: "14px" }}
                    /> */}
                  </Box>
                  Phone No.:
                  {user.user_emergency_contact}
                </Box>
              </Box>
            </Box>

            <Box h={{ base: "15px", md: "20px" }} />

            {/* Emergency Contact Office */}
            <Box
              className="id-desc-list"
              w="100%"
              p={{ base: "0 15px 0 25px", md: "0 20px 0 30px" }}
              textAlign="left"
            >
              <Text
                as="h4"
                className="id-desc-title"
                textAlign="left"
                fontFamily="'Fira Sans', sans-serif"
                fontSize={{ base: "11px", md: "12px" }}
                lineHeight="1.2"
                fontWeight="600"
                m="0 0 3px"
              >
                For Emergency Contact (Office):
              </Text>
              <Box as="ul" className="id-list" p="0" m="0" listStyleType="none">
                <Box
                  as="li"
                  display="inline-block"
                  w="100%"
                  position="relative"
                  // pl={{ base: "35px", md: "40px" }}
                  textAlign="left"
                  fontFamily="'Fira Sans', sans-serif"
                  fontSize={{ base: "11px", md: "12px" }}
                  lineHeight="2"
                >
                  <Box
                    as="span"
                    position="absolute"
                    left="0"
                    top="0"
                    // w={{ base: "35px", md: "40px" }}
                  >
                    {/* <Image
                      src="/asset/images/idcard/call_logo.svg"
                      alt="icon"
                      position="absolute"
                      pt="3px"
                      w={{ base: "12px", md: "14px" }}
                    /> */}
                  </Box>
                  Phone No.: {user.office_contact}
                </Box>
                <Box
                  as="li"
                  display="inline-block"
                  w="100%"
                  position="relative"
                  // pl={{ base: "35px", md: "40px" }}
                  textAlign="left"
                  fontFamily="'Fira Sans', sans-serif"
                  fontSize={{ base: "11px", md: "12px" }}
                  lineHeight="2"
                >
                  <Box
                    as="span"
                    position="absolute"
                    left="0"
                    top="0"
                    // w={{ base: "35px", md: "40px" }}
                  >
                    {/* <Image
                      src="/asset/images/idcard/email_logo.svg"
                      alt="icon"
                      position="absolute"
                      pt="3px"
                      w={{ base: "12px", md: "14px" }}
                    /> */}
                  </Box>
                  Email: info@ba-systems.com
                </Box>
              </Box>
            </Box>

            <br />

            {/* Return Note */}
            <Box
              className="id-nb-text"
              pt="0px"
              textAlign="center"
              fontFamily="'Fira Sans', sans-serif"
              w="100%"
              mt={{ base: "20px", md: "25px" }}
            >
              <Text fontSize={{ base: "11px", md: "12px" }}>
                If found, Please return the card to:
                <br />
                <Text as="strong">Business Automation Ltd.</Text>
              </Text>
            </Box>

            {/* Footer */}
            {/* <Box
              className="id-footer"
              position="absolute"
              left="0"
              right="0"
              bottom="0"
              h="26px"
              w="auto"
              display="flex"
              alignItems="center"
              justifyContent="center"
              backgroundColor="white"
              gap="0px"
            >
              <Image src="/asset/images/idcard/globe.svg" alt="logo" mr="0" />
              <Text 
                className="id-site-url"
                as="a"
                href="www.ba-systems.com"
                display="inline-block"
                position="relative"
                pl="30px"
                fontSize="12px"
                lineHeight="0px"
                fontWeight="bold"
                fontFamily="'Fira Sans', sans-serif"
                p="7px 10px 7px 10px"
                color="#3F51B5"
                textDecoration="none"
              >
                www.ba-systems.com
              </Text>
            </Box> */}
          </Box>
        </Box>
      </HStack>

      {/* Download Button */}
      {/* <Box position="absolute" bottom="20px">
        <Button
          colorScheme="blue"
          size="lg"
          onClick={downloadPDF}
          isLoading={isLoading}
          loadingText="Generating PDF..."
        >
          Download ID Card
        </Button>
      </Box> */}
    </Box>
  );
};

export default IDCardComponent;
